/**
 * Client-side layout-flag + UI-prefs hooks.
 *
 * `useLayout()` resolves once on mount and renders nothing until the answer
 * lands — the alternative is a v1→v2 flicker on every page load, which is
 * worse than a brief blank state. The result is cached in module state so
 * repeated calls inside the SPA reuse it.
 *
 * `useUiPrefs()` exposes a get + patch + put trio. Patches are optimistic:
 * the local state updates immediately and the network round-trip is
 * fire-and-forget. If the PATCH fails (e.g. 413 oversized doc), we refetch
 * to re-sync — the doc is small and a re-GET is cheap.
 */

import { useEffect, useState, useCallback } from 'react';

export type LayoutFlag = 'v1' | 'v2';

interface LayoutResponse {
  success: true;
  data: { layout: LayoutFlag };
}

let cachedLayout: LayoutFlag | null = null;
let inFlight: Promise<LayoutFlag> | null = null;

async function fetchLayout(): Promise<LayoutFlag> {
  if (cachedLayout) return cachedLayout;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const res = await fetch('/api/user/layout', { credentials: 'include' });
      if (!res.ok) return 'v1';
      const json = (await res.json()) as LayoutResponse;
      cachedLayout = json.data.layout;
      return cachedLayout;
    } catch {
      return 'v1';
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

export function useLayout(): LayoutFlag | null {
  const [layout, setLayout] = useState<LayoutFlag | null>(cachedLayout);
  useEffect(() => {
    if (cachedLayout) {
      setLayout(cachedLayout);
      return;
    }
    void fetchLayout().then(setLayout);
  }, []);
  return layout;
}

// ── UI Preferences ──────────────────────────────────────────────────────────

export interface UiPrefs {
  density?: 'comfortable' | 'compact';
  view_modes?: Record<string, 'grid' | 'list'>;
  rail_visible?: { dashboard?: boolean; detail?: boolean };
  banners_dismissed?: string[];
  onboarding?: { dismissed?: string[]; completed?: string[] };
  pinned_filters?: Record<string, Record<string, unknown>>;
  [k: string]: unknown;
}

const DEFAULT_PREFS: UiPrefs = {
  density: 'comfortable',
  view_modes: {},
  rail_visible: { dashboard: true, detail: true },
  banners_dismissed: [],
  onboarding: { dismissed: [], completed: [] },
  pinned_filters: {},
};

let cachedPrefs: UiPrefs | null = null;
const subscribers = new Set<(p: UiPrefs) => void>();

function notify(p: UiPrefs): void {
  cachedPrefs = p;
  for (const cb of subscribers) cb(p);
}

async function loadPrefs(): Promise<UiPrefs> {
  if (cachedPrefs) return cachedPrefs;
  try {
    const res = await fetch('/api/user/ui-prefs', { credentials: 'include' });
    if (!res.ok) {
      cachedPrefs = DEFAULT_PREFS;
      return DEFAULT_PREFS;
    }
    const json = (await res.json()) as { success: true; data: UiPrefs };
    cachedPrefs = json.data;
    return cachedPrefs;
  } catch {
    cachedPrefs = DEFAULT_PREFS;
    return DEFAULT_PREFS;
  }
}

export function useUiPrefs(): {
  prefs: UiPrefs;
  patch: (delta: Partial<UiPrefs>) => Promise<void>;
  ready: boolean;
} {
  const [prefs, setPrefs] = useState<UiPrefs>(cachedPrefs ?? DEFAULT_PREFS);
  const [ready, setReady] = useState<boolean>(cachedPrefs !== null);

  useEffect(() => {
    const sub = (p: UiPrefs): void => setPrefs(p);
    subscribers.add(sub);
    if (cachedPrefs === null) {
      void loadPrefs().then((p) => {
        setPrefs(p);
        setReady(true);
      });
    } else {
      setReady(true);
    }
    return () => {
      subscribers.delete(sub);
    };
  }, []);

  const patch = useCallback(async (delta: Partial<UiPrefs>): Promise<void> => {
    const optimistic = mergeShallow(cachedPrefs ?? DEFAULT_PREFS, delta);
    notify(optimistic);
    try {
      const res = await fetch('/api/user/ui-prefs', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(delta),
      });
      if (!res.ok) {
        // Re-sync from server on failure (413 oversized, etc).
        cachedPrefs = null;
        const fresh = await loadPrefs();
        notify(fresh);
      } else {
        const json = (await res.json()) as { success: true; data: UiPrefs };
        notify(json.data);
      }
    } catch {
      cachedPrefs = null;
      const fresh = await loadPrefs();
      notify(fresh);
    }
  }, []);

  return { prefs, patch, ready };
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function mergeShallow(base: UiPrefs, patch: Partial<UiPrefs>): UiPrefs {
  const out: Record<string, unknown> = { ...(base as Record<string, unknown>) };
  for (const [k, v] of Object.entries(patch)) {
    if (isPlainObject(v) && isPlainObject(out[k])) {
      out[k] = { ...(out[k] as Record<string, unknown>), ...v };
    } else {
      out[k] = v;
    }
  }
  return out as UiPrefs;
}

export { DEFAULT_PREFS };
